import React from "react";
import { Helmet } from "react-helmet";

const BASE_URL = "https://www.wecareehs.in";
const DEFAULT_IMAGE = `${BASE_URL}/wecare.png`;
const SITE_NAME = "WeCare EHS";

/**
 * SEOHead — Per-page dynamic meta tags component.
 *
 * Usage:
 *   <SEOHead
 *     title="ISO 45001 Training — WeCare EHS"
 *     description="Comprehensive ISO 45001 Occupational Health & Safety training..."
 *     keywords="ISO 45001, OHSMS, occupational health safety training India"
 *     canonical="/training/isotraining/iso45001"
 *   />
 *
 * Props:
 *   title       — Page title (shown in browser tab & Google results)
 *   description — Page description (shown in Google snippet, max 160 chars)
 *   keywords    — Comma-separated keywords (optional, minor SEO signal)
 *   canonical   — Relative path for canonical URL (e.g. "/training")
 *   image       — OG image URL (defaults to logo)
 *   type        — OG type ("website" | "article") — default: "website"
 *   noIndex     — Set true for private/auth pages (login, dashboard, admin)
 */
const SEOHead = ({
  title,
  description,
  keywords,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  noIndex = false,
}) => {
  const fullTitle = title
    ? title.includes(SITE_NAME)
      ? title
      : `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Safety Training, Audits & EHS Consultancy India`;

  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default SEOHead;
